import * as React from "react";
import {
    Create,
    Edit,
    SimpleForm,
    TextInput,
    NumberInput,
    BooleanInput,
    BooleanField,
    ReferenceInput,
    AutocompleteInput,
    SelectInput,
    FormDataConsumer,
    required,
    Toolbar,
    SaveButton,
    TextField,
    FunctionField,
    Datagrid,
    List,
    DeleteButton,
    EditButton,
} from 'react-admin';

const EditToolbar = props => (
    <Toolbar {...props}>
        <SaveButton />
    </Toolbar>
);

// Timesheet approvals and deal approvals are separate chains with their own
// stage numbers. An entry belongs to one of them for its whole life.
const typeChoices = [
    { id: 'TIMESHEET', name: 'Timesheet approvals' },
    { id: 'DEAL', name: 'Deal approvals' },
];

const typeLabel = type => (type === 'DEAL' ? 'Deal' : type === 'TIMESHEET' ? 'Timesheet' : '-');

const rowStyle = record => {
    if (record.stage === null || record.stage === undefined) {
        return { backgroundColor: '#fff9c4' };
    }
    return {};
};

const wildcardField = (
    <FunctionField
        label="Wildcard?"
        sortable={false}
        render={record => {
            if (record.wildcard_approval === null || record.wildcard_approval === undefined) {
                return '-';
            }
            return <BooleanField record={record} source="wildcard_approval" looseValue={true} />;
        }}
    />
);

const transform = data => ({
    user_id: data.approver && data.approver.user_id,
    role_name: data.approver && data.approver.role_name,
    can_undo_final_approval: (data.approver && data.approver.can_undo_final_approval) || false,
    stage: data.stage || 1,
    wildcard_approval: data.wildcard_approval || false,
    // an entry can't change chain, the server rejects it if this differs
    ...(data.type ? { type: data.type } : {}),
});

export const ProductionApprovalEntriesList = props => {
    const filters = [
        <SelectInput
            alwaysOn
            source="type"
            label="Approvals"
            choices={typeChoices}
            allowEmpty={false}
        />,
        <BooleanInput alwaysOn source="hide_unassigned" label="Hide unassigned" />,
    ];

    return (
        <List {...props} filters={filters} filterDefaultValues={{ type: 'TIMESHEET' }}>
            <Datagrid rowStyle={rowStyle} rowClick="edit">
                <TextField source="stage" label="Stage #" emptyText="-" />
                <FunctionField label="Type" sortable={false} render={record => typeLabel(record.type)} />
                <TextField source="approver.user.name" label="Approver" sortable={false} />
                <TextField source="approver.role_name" label="Role" sortable={false} emptyText="-" />
                {wildcardField}
                <FunctionField
                    label="Can Undo Final Approval?"
                    sortable={false}
                    render={record => record.type === 'DEAL'
                        ? '-'
                        : <BooleanField record={record} source="approver.can_undo_final_approval" looseValue={true} />}
                />
                <EditButton />
                <DeleteButton />
            </Datagrid>
        </List>
    );
};

export const ProductionApprovalEntriesCreate = props => (
    <Create {...props} title="New Production Approval Entry">
        <SimpleForm toolbar={<EditToolbar />} redirect="list" initialValues={{ type: 'TIMESHEET' }}>
            <SelectInput
                source="type"
                label="Approver for"
                choices={typeChoices}
                allowEmpty={false}
                validate={[required()]}
                helperText="Timesheet and deal approvals each have their own stages. Add the same person twice to approve both."
            />
            <ReferenceInput source="user_id" reference="users" label="User" perPage={1000} validate={[required()]}>
                <AutocompleteInput optionText="name" />
            </ReferenceInput>
            <TextInput source="role_name" label="Role Name" />
            <NumberInput source="stage" label="Stage" defaultValue={1} inputProps={{ min: 1 }} validate={[required()]} />
            <BooleanInput source="wildcard_approval" label="Wildcard Approver" />
            <FormDataConsumer>
                {({ formData }) => formData.type !== 'DEAL' && (
                    <BooleanInput source="can_undo_final_approval" label="Can Undo Final Approval" />
                )}
            </FormDataConsumer>
        </SimpleForm>
    </Create>
);

export const ProductionApprovalEntriesEdit = props => (
    <Edit {...props} title="Edit Production Approval Entry" transform={transform}>
        <SimpleForm toolbar={<EditToolbar />} redirect="list">
            <SelectInput
                source="type"
                label="Approver for"
                choices={typeChoices}
                disabled
                helperText="To move someone to the other chain, delete this entry and add them again."
            />
            <ReferenceInput source="approver.user_id" reference="users" label="User" perPage={1000} validate={[required()]}>
                <AutocompleteInput optionText="name" />
            </ReferenceInput>
            <TextInput source="approver.role_name" label="Role Name" />
            <NumberInput source="stage" label="Stage" defaultValue={1} inputProps={{ min: 1 }} validate={[required()]} />
            <BooleanInput source="wildcard_approval" label="Wildcard Approver" />
            <FormDataConsumer>
                {({ formData }) => formData.type !== 'DEAL' && (
                    <BooleanInput source="approver.can_undo_final_approval" label="Can Undo Final Approval" />
                )}
            </FormDataConsumer>
        </SimpleForm>
    </Edit>
);
